import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotaDoMural } from '@core/types';
import Layout from '../componentes/Layout';
import Carregando from '../componentes/Carregando';
import Aviso from '../componentes/Aviso';
import Segmentado from '../componentes/Segmentado';
import {
  colaboradoresDoMural, criarNota, editarNota, finalizarNota, muralDeNotas,
} from '../lib/consultas';
import {
  LIMITE_TEXTO, hexDe, montarGrade, ordenarNotas, quandoCurto, resumoDoMural,
} from '../lib/notas';
import { mensagemDeErro } from '../lib/supabase';

// O MURAL (migração 072). O papel colado no monitor do balcão, com autor, hora
// e uma cor por pessoa — e com um jeito de baixar o recado quando ele deixa de
// valer, que é o que o papel nunca teve.
//
// DUAS FORMAS, um conteúdo. No PC a grade é por PESSOA (colunas), porque foi
// assim que o dono pediu e porque de longe se lê a cor antes de ler a letra:
// "tem coisa nova na coluna do Renan". No celular vira UMA lista em ordem de
// chegada — cinco colunas de 5 cm não são cinco colunas, são cinco vielas, e
// quem está em pé com o cliente na frente lê o que chegou por último, não a
// coluna de quem escreveu.
//
// QUEM PODE O QUÊ não se decide aqui: `posso_finalizar` e `sou_autor` chegam
// prontos do banco (mural_de_notas). A tela desenha o botão que o servidor
// disse existir — as duas pontas concordarem por construção é o que evita o
// botão que aparece e falha na cara do vendedor.

type Modo = 'penduradas' | 'finalizadas';

const MODOS = [
  { id: 'penduradas', rotulo: 'No mural' },
  { id: 'finalizadas', rotulo: 'Finalizadas' },
] as const;

export default function Notas() {
  const cliente = useQueryClient();
  const [modo, setModo] = useState<Modo>('penduradas');
  const [escrevendo, setEscrevendo] = useState(false);
  const [texto, setTexto] = useState('');
  const [urgente, setUrgente] = useState(false);
  const [editando, setEditando] = useState<string | null>(null);

  // O mural é de todo mundo: o que o colega pendurou tem que aparecer aqui sem
  // ninguém apertar F5. Dois minutos é o intervalo que mantém o recado fresco
  // sem transformar a tela aberta o dia inteiro num pinga-pinga de requisição.
  const CADENCIA = { staleTime: 60_000, refetchInterval: 120_000 } as const;

  const notas = useQuery({
    queryKey: ['mural', modo],
    queryFn: () => muralDeNotas(modo === 'finalizadas'),
    ...CADENCIA,
  });

  const colunas = useQuery({
    queryKey: ['mural-colunas'],
    queryFn: colaboradoresDoMural,
    ...CADENCIA,
  });

  const recarregar = () => {
    void cliente.invalidateQueries({ queryKey: ['mural'] });
    void cliente.invalidateQueries({ queryKey: ['mural-colunas'] });
  };

  const pendurar = useMutation({
    mutationFn: () => criarNota(texto.trim(), urgente),
    onSuccess: () => {
      setTexto('');
      setUrgente(false);
      setEscrevendo(false);
      setModo('penduradas');
      recarregar();
    },
  });

  const baixar = useMutation({
    mutationFn: (p: { id: string; finalizar: boolean }) => finalizarNota(p.id, p.finalizar),
    onSuccess: recarregar,
  });

  const salvarEdicao = useMutation({
    mutationFn: (p: { id: string; texto: string; urgente: boolean }) =>
      editarNota(p.id, p.texto.trim(), p.urgente),
    onSuccess: () => { setEditando(null); recarregar(); },
  });

  const lista = notas.data ?? [];
  const grade = useMemo(
    () => montarGrade(colunas.data ?? [], lista),
    [colunas.data, lista],
  );
  const emOrdem = useMemo(() => ordenarNotas(lista), [lista]);
  const resumo = resumoDoMural(lista);
  const agora = new Date();

  const carregando = notas.isLoading || colunas.isLoading;
  const erro = notas.error ?? colunas.error ?? pendurar.error ?? baixar.error
    ?? salvarEdicao.error;

  const cartao = (nota: NotaDoMural) => (
    <CartaoNota
      key={nota.id}
      nota={nota}
      agora={agora}
      editando={editando === nota.id}
      ocupado={baixar.isPending || salvarEdicao.isPending}
      aoEditar={() => setEditando(nota.id)}
      aoCancelarEdicao={() => setEditando(null)}
      aoSalvar={(t, u) => salvarEdicao.mutate({ id: nota.id, texto: t, urgente: u })}
      aoFinalizar={(finalizar) => baixar.mutate({ id: nota.id, finalizar })}
    />
  );

  return (
    <Layout
      titulo="Notas"
      acao={
        <button type="button" className="botao-primario px-3 py-2 text-sm"
                onClick={() => setEscrevendo((v) => !v)}>
          {escrevendo ? 'Fechar' : 'Nova nota'}
        </button>
      }
    >
      <div className="mx-auto max-w-5xl space-y-4 p-4">
        <p className="text-sm text-mute">
          O mural da loja: o que todo mundo precisa saber fica pendurado aqui até alguém dar
          conta. Um orçamento fechado fora do padrão, uma peça que não pode sair, um lembrete
          para o time. Quem escreveu — ou a gerência — finaliza, e a nota sai da grade.
        </p>

        {escrevendo && (
          <section className="cartao space-y-3 p-4">
            <div>
              <label className="rotulo" htmlFor="nova-nota">Sua nota</label>
              <textarea
                id="nova-nota"
                className="campo min-h-28"
                value={texto}
                maxLength={LIMITE_TEXTO}
                placeholder="Ex.: Orçamento 3412 fechado a R$ 890 (abaixo do padrão) — combinado com o dono."
                onChange={(e) => setTexto(e.target.value)}
              />
              <p className="mt-1 text-right text-xs text-dim">
                {texto.length}/{LIMITE_TEXTO}
              </p>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="h-5 w-5"
                     checked={urgente} onChange={(e) => setUrgente(e.target.checked)} />
              <span>Marcar como <span className="selo selo-hoje">urgente</span></span>
            </label>

            <button type="button" className="botao-primario w-full"
                    disabled={pendurar.isPending || texto.trim() === ''}
                    onClick={() => pendurar.mutate()}>
              {pendurar.isPending ? 'Pendurando…' : 'Pendurar no mural'}
            </button>
          </section>
        )}

        {erro ? <Aviso tipo="erro">{mensagemDeErro(erro)}</Aviso> : null}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <Segmentado itens={MODOS} valor={modo} aoMudar={setModo} />
          {modo === 'penduradas' && !carregando && (
            <p className="text-xs text-dim">
              {resumo.penduradas === 0 ? 'nada pendurado'
                : `${resumo.penduradas} no mural`}
              {resumo.urgentes > 0 && (
                <span className="text-alerta-claro">
                  {' · '}<span className="ponto mr-1 align-middle text-alerta" aria-hidden="true" />
                  {resumo.urgentes} urgente{resumo.urgentes > 1 ? 's' : ''}
                </span>
              )}
            </p>
          )}
        </div>

        {carregando ? <Carregando texto="Abrindo o mural" /> : lista.length === 0 ? (
          <Aviso tipo="info">
            {modo === 'penduradas'
              ? 'Nenhuma nota pendurada. O mural vazio é boa notícia.'
              : 'Nenhuma nota finalizada ainda.'}
          </Aviso>
        ) : modo === 'finalizadas' ? (
          /* O HISTÓRICO NÃO É GRADE. Grade responde "o que está pendurado na
             coluna de quem"; aqui a pergunta é outra — "o que saiu do mural, e
             quando" —, e isso é linha do tempo. Vem do banco já na ordem de
             quem baixou por último, e por isso não passa por ordenarNotas:
             reordenar por urgência o que já foi resolvido seria dar destaque a
             um grito que já acabou. */
          <div className="space-y-3">{lista.map(cartao)}</div>
        ) : (
          <>
            {/* PC: a grade por pessoa. auto-fit para a loja poder crescer sem
                ninguém mexer aqui — com cinco pessoas dá cinco colunas, com
                nove ele quebra em duas fileiras em vez de espremer. */}
            <div
              className="hidden gap-4 md:grid"
              style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(15rem, 1fr))' }}
            >
              {grade.map((coluna) => (
                <section key={coluna.id} className="min-w-0 space-y-3">
                  <header
                    className="flex items-baseline justify-between gap-2 border-b-2 pb-2"
                    style={{ borderColor: coluna.hex }}
                  >
                    <h2 className="truncate font-bold" style={{ color: coluna.hex }}>
                      {coluna.nome}
                      {!coluna.ativo && (
                        <span className="ml-1 text-xs font-normal text-dim">(desligado)</span>
                      )}
                    </h2>
                    <span className="shrink-0 font-mono text-xs text-dim">
                      {coluna.notas.length}
                    </span>
                  </header>
                  {coluna.notas.length === 0
                    ? <p className="text-xs text-dim">—</p>
                    : coluna.notas.map(cartao)}
                </section>
              ))}
            </div>

            {/* Celular: uma lista só, na ordem em que as coisas chegaram. */}
            <div className="space-y-3 md:hidden">
              {emOrdem.map(cartao)}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

/**
 * Uma nota. A faixa da esquerda é a cor do autor — no celular ela é a ÚNICA
 * pista de quem escreveu antes de a pessoa ler o nome, e é por isso que ela
 * existe também no PC, onde a coluna já diria: a mesma nota tem que parecer a
 * mesma coisa nos dois lugares.
 */
function CartaoNota({
  nota, agora, editando, ocupado, aoEditar, aoCancelarEdicao, aoSalvar, aoFinalizar,
}: {
  nota: NotaDoMural;
  agora: Date;
  editando: boolean;
  ocupado: boolean;
  aoEditar: () => void;
  aoCancelarEdicao: () => void;
  aoSalvar: (texto: string, urgente: boolean) => void;
  aoFinalizar: (finalizar: boolean) => void;
}) {
  // Sem useEffect de propósito: o rascunho é semeado no clique de "Editar"
  // (abaixo), com o texto que está na tela naquele instante. Efeito que
  // sincroniza formulário com servidor é o bug da folha — resposta lenta
  // chegando depois apaga o que a pessoa digitou.
  const [rascunho, setRascunho] = useState(nota.texto);
  const [urgente, setUrgente] = useState(nota.urgente);
  const cor = hexDe({ id: nota.autor_id, cor: nota.autor_cor });
  const pendurada = nota.finalizada_em === null;

  return (
    <article
      className={`border border-l-4 border-edge p-3 ${
        nota.urgente && pendurada ? 'bg-panel-alt' : 'bg-panel'}`}
      style={{ borderLeftColor: cor }}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1">
        <span className="truncate text-sm font-bold" style={{ color: cor }}>
          {nota.autor_nome}
        </span>
        <span className="font-mono text-[0.6875rem] text-dim">
          {quandoCurto(nota.criada_em, agora)}
          {nota.editada_em && ' · editada'}
        </span>
      </header>

      {nota.urgente && pendurada && (
        <p className="mt-1">
          {/* O sinal "hoje" do Grafite: âmbar no selo, com a palavra. O fundo
              do cartão não pinta — sinal mora no selo, não no bloco. */}
          <span className="selo selo-hoje">
            <span className="ponto" aria-hidden="true" />
            urgente
          </span>
        </p>
      )}

      {editando ? (
        <div className="mt-2 space-y-2">
          <textarea
            className="campo min-h-24 text-sm"
            value={rascunho}
            maxLength={LIMITE_TEXTO}
            onChange={(e) => setRascunho(e.target.value)}
          />
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" className="h-4 w-4"
                   checked={urgente} onChange={(e) => setUrgente(e.target.checked)} />
            Urgente
          </label>
          <div className="flex gap-2">
            <button type="button" className="botao-primario flex-1 px-3 py-2 text-sm"
                    disabled={ocupado || rascunho.trim() === ''}
                    onClick={() => aoSalvar(rascunho, urgente)}>
              Salvar
            </button>
            <button type="button" className="border border-edge px-3 py-2 text-sm text-mute"
                    onClick={() => { setRascunho(nota.texto); setUrgente(nota.urgente); aoCancelarEdicao(); }}>
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <p className="mt-2 text-sm whitespace-pre-wrap break-words">{nota.texto}</p>
      )}

      {!pendurada && (
        <p className="mt-2 border-t border-edge pt-2 text-xs text-dim">
          Finalizada {quandoCurto(nota.finalizada_em ?? nota.criada_em, agora)}
          {nota.finalizada_por_nome && ` por ${nota.finalizada_por_nome}`}
        </p>
      )}

      {!editando && (nota.posso_finalizar || (nota.sou_autor && pendurada)) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {nota.sou_autor && pendurada && (
            <button
              type="button"
              className="border border-edge px-3 py-1.5 text-xs text-mute"
              onClick={() => {
                setRascunho(nota.texto);
                setUrgente(nota.urgente);
                aoEditar();
              }}
            >
              Editar
            </button>
          )}
          {nota.posso_finalizar && (
            <button
              type="button"
              className="border border-edge px-3 py-1.5 text-xs font-bold"
              disabled={ocupado}
              onClick={() => aoFinalizar(pendurada)}
            >
              {pendurada ? 'Finalizar ✓' : 'Devolver ao mural'}
            </button>
          )}
        </div>
      )}
    </article>
  );
}
